import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ProgramDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [slug]);
  if (!p) return <div className="min-h-screen grid place-items-center text-[#000000]/40 small-caps">Loading…</div>;

  const specs = [
    { k: "Minimum commitment", v: `$${(p.min_amount / 1000).toLocaleString()}K` },
    { k: "Maximum commitment", v: p.max_amount >= 1_000_000 ? `$${(p.max_amount / 1_000_000).toFixed(0)}M` : `$${(p.max_amount / 1000).toLocaleString()}K` },
    { k: "Time to close", v: p.time_to_close },
    { k: "Typical pricing", v: p.typical_rate },
    { k: "Minimum revenue", v: p.min_revenue ? `$${(p.min_revenue / 1000).toLocaleString()}K` : "Situational" },
  ];

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="hair-b bg-[#FFFFFF]/85 backdrop-blur">
        <div className="mx-auto max-w-[1360px] px-8 py-5 flex items-center justify-between">
          <Logo />
          <Link to={`/register?product=${p.slug}`} className="small-caps text-[#000000] border-b border-[#000000] pb-0.5" data-testid="program-apply-btn">
            Request Engagement →
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1360px] px-8 py-20">
        <Link to="/" className="small-caps text-[#000000]/55 hover:text-[#000000]" data-testid="back-home-link">← All mandates</Link>

        <div className="mt-14 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="small-caps text-[#000000]">{p.category}</div>
            <h1 className="mt-6 font-semibold text-[72px] lg:text-[104px] leading-[0.95] tracking-tight text-[#000000]">
              {p.name}
            </h1>
            <p className="mt-10 max-w-2xl text-[19px] leading-[1.6] text-[#000000]/75 font-light">
              {p.description}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-8 lg:border-l lg:border-[#000000]/12">
            <div className="small-caps text-[#000000]/55 mb-6">Terms</div>
            <dl className="space-y-5">
              {specs.map((s) => (
                <div key={s.k} className="flex items-baseline justify-between gap-6 hair-b pb-4">
                  <dt className="small-caps text-[#000000]/55">{s.k}</dt>
                  <dd className="font-semibold text-[18px] text-[#000000] text-right">{s.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-6">
            <div className="small-caps text-[#000000]/55">Diligence Requirements</div>
            <h3 className="mt-4 font-semibold text-[32px] text-[#000000]">Documents anticipated</h3>
            <ul className="mt-8">
              {(p.documents || []).map((d, i) => (
                <li key={d} className="grid grid-cols-12 items-baseline py-4 hair-b">
                  <span className="col-span-1 font-mono text-[11px] text-[#000000]/40">{String(i + 1).padStart(2, "0")}</span>
                  <span className="col-span-11 font-semibold text-[18px] text-[#000000]/85">{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:pl-12">
            <div className="small-caps text-[#000000]/55">Sector Focus</div>
            <h3 className="mt-4 font-semibold text-[32px] text-[#000000]">Industries considered</h3>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {(p.industries || []).map((i) => (
                <div key={i} className="font-semibold text-[20px] text-[#000000]/80">— {i}</div>
              ))}
            </div>

            <div className="mt-16">
              <div className="small-caps text-[#000000]/55">Frequently Asked</div>
              <Accordion type="single" collapsible className="mt-6">
                {(p.faqs || []).map((f, idx) => (
                  <AccordionItem key={idx} value={`f-${idx}`} className="border-b border-[#000000]/12">
                    <AccordionTrigger className="text-left font-semibold text-[20px] text-[#000000] hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[15px] text-[#000000]/70 leading-[1.7]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>

        <div className="mt-28 hair-t pt-14 flex items-center justify-between">
          <div>
            <div className="small-caps text-[#000000]/55">Initiate</div>
            <h4 className="mt-3 font-semibold text-[36px] text-[#000000]">
              Ready to engage on <span className="italic">{p.name.toLowerCase()}</span>?
            </h4>
          </div>
          <Link to={`/register?product=${p.slug}`} className="group inline-flex items-center gap-3 text-[#000000]" data-testid="program-cta-apply-btn">
            <span className="h-[1px] w-16 bg-[#000000] transition-all group-hover:w-24" />
            <span className="small-caps">Request Engagement</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
